import * as React from 'react';
import Modal from 'antd/lib/modal';
import Input from 'antd/lib/input';
import Alert from 'antd/lib/alert';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { DeleteOutlined } from '@ant-design/icons';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { useCallUpdatable, useGetEntity } from '@/utils/hooks/helpers';
import { StepConfigUpdatable } from '@/actions/stepConfig';
import { globalMessage } from '@/utils/globalMessage';
import { RunwayResponseType, ConfigFileSection } from '@/types';
import { FetchableEntityType } from '@/entityType';

interface DeleteStepLinkProps {
    orgId: string;
    appId: string;
    workflowId: string;
    stepConfigId: string;
    stepName: string;
    onDeleted?: () => void;
}

export const DeleteStepLink: React.FC<DeleteStepLinkProps> = ({
    orgId,
    appId,
    workflowId,
    stepConfigId,
    stepName,
    onDeleted,
}) => {
    const [fetchAppState] = useGetEntity(FetchableEntityType.AppState, appId);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [typedName, setTypedName] = React.useState('');

    const [deleteStepConfig, isDeleting, deleteResponseType, deleteError] = useCallUpdatable(
        StepConfigUpdatable.DeleteStepConfig,
        { orgId, workflowId, stepConfigId },
        stepConfigId,
    );

    const didHandleDeleteSuccessRef = React.useRef(false);
    React.useEffect(() => {
        const didSucceed = deleteResponseType === RunwayResponseType.Success;
        if (didSucceed && !didHandleDeleteSuccessRef.current) {
            didHandleDeleteSuccessRef.current = true;
            globalMessage.success(`${stepName} deleted`);
            setIsModalOpen(false);
            fetchAppState({ orgId, appId });
            onDeleted?.();
        }
        if (!didSucceed) {
            didHandleDeleteSuccessRef.current = false;
        }
    }, [deleteResponseType, stepName, onDeleted, fetchAppState, orgId, appId]);

    React.useEffect(() => {
        if (deleteError) {
            globalMessage.error(deleteError);
        }
    }, [deleteError]);

    return (
        <>
            <PermissionedActionButton
                actionType={RunwayUserAction.UAUpdateAppSettings}
                configFileSection={ConfigFileSection.Flightpath}
                type="link"
                icon={<DeleteOutlined />}
                // Matches the icon gap of the menu items above, which is wider than a button's default.
                style={{ paddingInline: 0, columnGap: 10 }}
                onClick={() => {
                    setTypedName('');
                    setIsModalOpen(true);
                }}
                danger
            >
                Delete step
            </PermissionedActionButton>

            <Modal
                open={isModalOpen}
                title={`Delete '${stepName}'?`}
                okText="Delete step"
                okButtonProps={{ danger: true, loading: isDeleting, disabled: typedName !== stepName }}
                onOk={() => deleteStepConfig()}
                onCancel={() => setIsModalOpen(false)}
                destroyOnClose
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Alert
                        showIcon
                        type="error"
                        message="This is a destructive change. The step will be deleted from all releases, including past completed ones."
                    />

                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text>Type the step name to confirm:</Text>
                        <Input
                            value={typedName}
                            onChange={(event) => setTypedName(event.target.value)}
                            placeholder={stepName}
                        />
                    </Space>
                </Space>
            </Modal>
        </>
    );
};
